import {Feature} from '../reducers/environmentReducer';
import {$qs} from '../../../@shared/ts/dom';
import {FeatureFlag} from '../../../@type/features';
import {Loader} from '@googlemaps/js-api-loader';
import {PeachPayCustomer} from '../reducers/peachPayCustomerReducer';
import {PeachPayOrder} from '../reducers/peachPayOrderReducer';

function shouldEnableAddressAutocomplete() {
	if (Feature.enabled(FeatureFlag.ADDRESS_AUTOCOMPLETE)) {
		const activeLocations = Feature.metadata(FeatureFlag.ADDRESS_AUTOCOMPLETE, 'active_locations');

		if (activeLocations === 'default' || activeLocations === 'express_checkout_only') {
			return true;
		}
	}

	return false;
}

export function initAddressAutocomplete() {
	if (!shouldEnableAddressAutocomplete()) {
		return;
	}

	const loader = new Loader({
		apiKey: 'AIzaSyAL_j0UvKJacQhZ3OedCj_bkcTbuO63MVU',
		version: 'weekly',
		libraries: ['places'],
	});

	loader.importLibrary('places').then(async () => {
		const autocompleteOptions = {
			fields: ['address_components'],
			types: ['address'],
		};

		const billingInput = $qs<HTMLInputElement>('#pp-billing-form [name="billing_address_1"]');
		if (billingInput) {
			const billingAddressAutocomplete = new google.maps.places.Autocomplete(billingInput, autocompleteOptions);
			billingAddressAutocomplete?.addListener('place_changed', async () => {
				const place = billingAddressAutocomplete.getPlace();
				await fillInAddress(place, 'billing');
			});
		}

		const shippingInput = $qs<HTMLInputElement>('#pp-shipping-form [name="shipping_address_1"]');
		if (shippingInput) {
			const shippingAddressAutocomplete = new google.maps.places.Autocomplete(shippingInput, autocompleteOptions);
			shippingAddressAutocomplete?.addListener('place_changed', async () => {
				const place = shippingAddressAutocomplete.getPlace();
				await fillInAddress(place, 'shipping');
			});
		}
	}).catch(error => {
		console.log('An error occurred during Google Maps Places API importLibrary:', error);
	});
}

async function fillInAddress(place: google.maps.places.PlaceResult, addressType: 'billing' | 'shipping'): Promise<void> {
	const form = addressType === 'billing' ? PeachPayCustomer.billing : PeachPayCustomer.shipping;

	//  "street_number" + "route" is the street address
	const streetNumber = place.address_components?.find(
		component => component.types[0] === 'street_number',
	)?.long_name ?? '';
	const route = place.address_components?.find(
		component => component.types.includes('route'),
	)?.long_name ?? '';
	form.address1(`${streetNumber} ${route}`, true);

	// "subpremise" is the apartment number
	const subpremise = place.address_components?.find(
		component => component.types.includes('subpremise'),
	)?.long_name ?? '';
	form.address2(subpremise, true);

	// "locality","political" is the city
	const city = place.address_components?.find(
		component => component.types.includes('locality')// For US
									|| component.types.includes('postal_town'), // For UK
	)?.long_name ?? '';
	form.city(city, true);

	// "postal_code" is the zip code
	const postalCode = place.address_components?.find(
		component => component.types.includes('postal_code'),
	)?.long_name ?? '';
	form.postal(postalCode, true);

	// "country", "political" is the country
	const country = place.address_components?.find(
		component => component.types.includes('country'),
	)?.short_name ?? '';
	form.country(country, true);

	// "administrative_area_level_1", "political" is the state
	const state = place.address_components?.find(
		component => component.types.includes('administrative_area_level_1'),
	)?.short_name?.split('.').join('') ?? '';
	form.state(state, true);

	await (addressType === 'billing' ? PeachPayOrder.billing : PeachPayOrder.shipping)?.reportValidity();
}
